// src/api/giftcardsApi.ts
// Adaptador del módulo TARJETAS DE REGALO contra lealify-base (Inc12). Reemplaza el
// flujo Firebase (businesses/{id}.giftCardConfig + pointsRecharges + webhook n8n).
// El backend acredita al instante si el teléfono ya es cliente, o deja la tarjeta
// 'pending' para reclamar en el kiosko; el aviso por WhatsApp lo manda base (shadow
// si el negocio no tiene WhatsApp). La imagen de la tarjeta (Canvas) se genera en el
// panel y se envía como data-URI → base la manda por sendImage.
import { baseApi } from './baseClient';
import type { GiftCardCustomDesign } from '../features/gift-cards/utils/giftCardTemplates';

export interface GiftCardSettings {
  enabled: boolean;
  template: string;
  google_maps_url: string;
  // Diseño propio del negocio (fondo + campos con coordenadas). Solo existe si
  // se dio de alta para el tenant; el panel muestra la opción "⭐" cuando viene.
  custom_design?: GiftCardCustomDesign | null;
  /** 14-oct-2026 (Marrieto #1400): 'points' (default, como siempre) = la tarjeta regala PUNTOS;
   *  'money' = regala PESOS al saldo de regalo, que la caja cobra como forma de pago «Gift card». */
  mode?: GiftCardMode;
}

export type GiftCardMode = 'points' | 'money';

/** Un movimiento del saldo de regalo (pesos, con signo). */
export interface GiftBalanceMovement {
  id: number;
  type: 'carga' | 'cobro' | 'reverso';
  amount: number;
  balance_after: number;
  gift_card_id: number | null;
  ticket_ref: string | null;
  reversed_at: string | null;
  source: string | null;
  note: string | null;
  created_at: string;
  user_name: string | null;
  branch_name: string | null;
}

export interface GiftCard {
  id: number;
  recipient_phone: string;
  recipient_name: string | null;
  sender_name: string | null;
  points: number;
  /** En qué se regaló: 'money' = `points` son PESOS de saldo de regalo. Ausente = puntos. */
  unit?: GiftCardMode;
  /** Mensaje personal de quien regala (se agrega al texto del WhatsApp). 14-oct-2026. */
  message?: string | null;
  template_id: string | null;
  status: 'pending' | 'redeemed' | 'cancelled';
  created_at: string;
  redeemed_at: string | null;
}

export interface CreateGiftCardInput {
  recipient_phone: string;
  points: number;
  recipient_name?: string;
  sender_name?: string;
  /** Mensaje personal (≤300): base lo AGREGA al final del texto de la plantilla, no la reemplaza. */
  message?: string;
  template_id?: string;
  image?: string;        // data-URI de la tarjeta generada (opcional)
}

export interface CreateGiftCardResult {
  id: number;
  status: 'pending' | 'redeemed';
  credited: boolean;       // true si se acreditó al instante (ya era cliente)
  contact_id: number | null;
  balance: number | null;  // saldo tras acreditar (si credited): PUNTOS, o PESOS de regalo si unit='money'
  unit?: GiftCardMode;
  name?: string | null;
  whatsapp?: { ok: boolean; shadow?: boolean; detail?: string };
}

export const giftcardsApi = {
  getSettings: () => baseApi.get<{ settings: GiftCardSettings; your_level: string }>('/giftcards/settings'),
  updateSettings: (patch: Partial<GiftCardSettings>) =>
    baseApi.put<{ settings: GiftCardSettings }>('/giftcards/settings', patch),

  list: (status?: 'pending' | 'redeemed' | 'cancelled') =>
    baseApi.get<{ gift_cards: GiftCard[] }>(`/giftcards${status ? `?status=${status}` : ''}`),

  create: (input: CreateGiftCardInput) => baseApi.post<CreateGiftCardResult>('/giftcards', input),

  cancel: (id: number) => baseApi.post<{ ok: true }>(`/giftcards/${id}/cancel`),

  pending: (phone: string) =>
    baseApi.get<{ has_pending: boolean; count: number }>(`/giftcards/pending?phone=${encodeURIComponent(phone)}`),

  // Envío de prueba (botón de la config). No crea tarjeta ni toca el ledger.
  testSend: (payload: { phone: string; template?: string; image?: string }) =>
    baseApi.post<{ ok: boolean; shadow?: boolean; detail?: string }>('/giftcards/test', payload),

  // Saldo de regalo de un cliente (ficha). available=false → el servidor aún no tiene la función.
  balance: (contactId: string | number) =>
    baseApi.get<{ available: boolean; mode?: GiftCardMode; balance: number; movements: GiftBalanceMovement[] }>(
      `/giftcards/balance/${encodeURIComponent(String(contactId))}`),
  // Carga MANUAL en pesos (manage): p. ej. pasar a saldo de regalo una gift card vendida en puntos.
  loadBalance: (contactId: string | number, amount: number, note?: string) =>
    baseApi.post<{ ok: boolean; movement_id: number; balance: number }>(
      `/giftcards/balance/${encodeURIComponent(String(contactId))}/load`, { amount, ...(note ? { note } : {}) }),
};

export default giftcardsApi;
